import React, { Component } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import { COLOR } from '../../utils/colorsAndFilters'

// Formats a £ cost as pence with £ in brackets, eg "0.31p (£0.0031)" - mirrors
// Monitoring_EmailBody.cs's FormatGeminiCostGbp so the modal and the dev email agree
const formatGeminiCostGbp = costGbp => `${(costGbp * 100).toFixed(2)}p (£${costGbp.toFixed(4)})`

// Gemini returns the synopsis as one item per line (see GeminiClient.SynopsisAsync's prompt) -
// splitting it out lets each item render as its own list row instead of one dense paragraph
// (a plain string with embedded "\n"s collapses to a single line in HTML/JSX)
const getSynopsisItems = synopsis => (synopsis || '').split('\n').map(line => line.trim()).filter(Boolean)

// Mirrors Monitoring_EmailBody.cs's "d/M/yy" date formatting so the modal's document list agrees with the email
const formatReleaseDate = dateString => {
    const date = new Date(dateString)
    return `${date.getDate()}/${date.getMonth() + 1}/${String(date.getFullYear()).slice(-2)}`
}

// Mirrors Monitoring_EmailBody.cs's file-extension extraction (substring after the last '.' in the download link)
const getFileExtension = downloadLink => downloadLink.slice(downloadLink.lastIndexOf('.') + 1).toLowerCase()

// Mirrors Monitoring_EmailBody.cs's company-name trimming (strips a trailing ";" some LSE company names carry)
const formatCompanyName = companyName => (companyName || '').trimEnd().replace(/;+$/, '')

// Mirrors Monitoring_EmailBody.cs's "dddd dd/MM/yyyy" title date formatting, eg "Tuesday 18/08/2026"
const formatTitleDate = () => {
    const date = new Date()
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${date.toLocaleDateString('en-GB', { weekday: 'long' })} ${day}/${month}/${date.getFullYear()}`
}

// AI synopsis box - shown whenever the backend returned one, whether or not any releases
// matched (eg no documents matched the search terms at all, or none were found relevant -
// see Monitoring_Synopsis.cs's SynopsisReleasesAsync, which returns a fixed "no relevant
// disclosures" message in either case rather than leaving the modal silent)
const renderSynopsis = synopsis => synopsis && (
    <div style={{ marginTop: 16, backgroundColor: COLOR.BRAND.LIGHT, borderRadius: 4, padding: '8px 16px' }}>
        <div style={{ fontWeight: 'bold' }}>AI synopsis</div>
        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
            {getSynopsisItems(synopsis).map((item, index) => (
                <li key={index} style={{ marginTop: 4 }}>{item}</li>
            ))}
        </ul>
    </div>
)

export default class MonitorSearchModal extends Component {

    render() {
        // console.log('MonititorSearchModal this.props:', this.props)
        const { results, error, maxDocumentsExceeded, synopsis, geminiUsage } = this.props

        var numReleases = 0
        var numSubstrings = 0

        if (!error && results) {
            numReleases = results.length

            results.forEach((result) => {
                if (result.SubstringsNew)
                    result.SubstringsNew.forEach((substring) => {
                        numSubstrings += substring.length ? substring.length : 1
                    })
                else
                    result.Substrings.forEach((substring) => {
                        numSubstrings += substring.length ? substring.length : 1
                    })
            })
        }

        if (!this.props.open)
            return null

        let title = error ? 'Unable to run test' : (results === null ? 'Searching... please wait' : `Regulatory Disclosures Monitoring on ${formatTitleDate()}`)

        return (
            <Dialog open={Boolean(this.props.open)} onClose={this.props.onHide} maxWidth="sm" fullWidth>
                {title && <DialogTitle>{title}</DialogTitle>}

                {error && <DialogContent>
                    <div>{error}</div>
                </DialogContent>}

                {!error && results === null && <DialogContent>
                    <div>Depending on your settings, the search may take a minute or so.</div>
                    <div>You will receive an email shortly after completion.</div>
                </DialogContent>}

                {!error && results && numReleases > 0 && <DialogContent>
                    {maxDocumentsExceeded && <div style={{ fontWeight: 'bold', color: 'red' }}>{`Only ${numReleases} disclosures were analysed as this is the maximum.`}</div>}

                    {renderSynopsis(synopsis)}

                    <div style={{ marginTop: 16 }}>{`Your search terms appeared ${numSubstrings} times in ${numReleases} disclosures.`}</div>

                    {/* Document list, mirroring the layout/content of the email body (Monitoring_EmailBody.cs):
                        company + index, headline link with published/submitted/type, matched context
                        snippets (or the "unable to supply context" fallback), then the AI relevance line */}
                    <div style={{ marginTop: 16 }}>
                        {results.map((result, index) => (
                            <div key={index} style={{ marginTop: 12 }}>
                                <div style={{ paddingTop: 6 }}>
                                    {`${index + 1}. `}
                                    <b>{formatCompanyName(result.CompanyName)}</b>
                                    {` (${result.LseIndex})`}
                                    {result.AiProbability != null && ` ${Math.round(result.AiProbability)}%`}
                                </div>
                                {result.AiProbability != null && <div style={{ paddingLeft: 15, marginTop: 2, marginBottom: 8 }}>
                                    {result.AiReason}
                                </div>}
                                {/* AI analysis failed for this release after retries (see Monitoring_Analyse.cs) -
                                    shown in red in place of the (never-set) relevance line above */}
                                {result.AiProbability == null && result.AiError && <div style={{ paddingLeft: 15, marginTop: 2, marginBottom: 8, color: 'red' }}>
                                    {result.AiError}
                                </div>}
                                <div style={{ paddingLeft: 15 }}>
                                    <a href={result.DownloadLink} target="_blank" rel="noopener noreferrer">{result.Headline}</a>
                                    <div style={{ fontSize: 10 }}>
                                        {`Published: ${formatReleaseDate(result.Published)}  Submitted: ${formatReleaseDate(result.Submitted)}  Type: "${getFileExtension(result.DownloadLink)}"`}
                                    </div>
                                </div>
                                {result.Keywords.map((keyword, keywordIndex) => {
                                    const contexts = (result.SubstringsNew && result.SubstringsNew[keywordIndex]) || []
                                    return contexts.length > 0
                                        ? contexts.map((context, contextIndex) => (
                                            // Contexts contain deliberate <b>/<a> highlighting markup added by
                                            // Monitoring_GetSubstrings.cs (mirrors the email's raw-HTML rendering)
                                            <div key={`${keywordIndex}-${contextIndex}`} style={{ fontSize: 12, paddingLeft: 15, marginTop: 5 }} dangerouslySetInnerHTML={{ __html: `"${context}"` }} />
                                        ))
                                        : <div key={keywordIndex} style={{ fontSize: 12, paddingLeft: 15, marginTop: 5 }}><b>{keyword}</b> (unable to supply context)</div>
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Dev-only in practice: only present when a Gemini call was actually made this
                        run (Analyse and/or Synopsis enabled), same as AiProbability/synopsis above.
                        Placed last, at the bottom, to match the email body (Monitoring_EmailBody.cs) */}
                    {geminiUsage && geminiUsage.Calls && geminiUsage.Calls.length > 0 && <div style={{ marginTop: 16, fontSize: 12 }}>
                        <div style={{ fontWeight: 'bold' }}>{`Gemini usage (model: ${geminiUsage.Calls[0].Model})`}</div>
                        {geminiUsage.Calls.map((call, index) => (
                            <div key={index}>
                                {`${call.Call}: ${call.PromptTokens ?? '?'} prompt + ${(call.CandidatesTokens ?? 0) + (call.ThoughtsTokens ?? 0)} output tokens (${call.TotalTokens ?? '?'} total), ${formatGeminiCostGbp(call.CostGbp ?? 0)}, ${call.ElapsedMs}ms`}
                            </div>
                        ))}
                        <div style={{ fontWeight: 'bold', marginTop: 4 }}>
                            {`Total: ${geminiUsage.TotalPromptTokens} prompt + ${geminiUsage.TotalOutputTokens} output tokens, ${formatGeminiCostGbp(geminiUsage.TotalCostGbp)}, ${geminiUsage.TotalElapsedMs}ms`}
                        </div>
                    </div>}
                </DialogContent>}

                {!error && results && numReleases === 0 && <DialogContent>
                    {renderSynopsis(synopsis)}
                    <div style={{ marginTop: synopsis ? 16 : 0 }}>{`Your search terms did not appear in any regulatory disclosures from companies in the groups you selected.`}</div>
                </DialogContent>}

                {<DialogActions style={{ justifyContent: 'space-between' }}>
                    <div>{!error && results && numReleases > 0 && 'Please check your email for details.'}</div>
                    <Button onClick={this.props.onClose} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>}
            </Dialog>
        )
    }
}
